import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as yup from 'yup';
import QText from './QText';
import TextInput from './TextInput';
import Button from './Button';
import InternationalPhonePicker from './InternationPhonePicker';
import { setLoading } from '../store/slice/loadingSlice';
import { getToken, setFcmToken } from '../utils';
import { getFirebaseToken, isSupportedForWeb } from '../../firebase';

const MobileNumberForm = ({
  queueUserDetail,
  isDirectAccess = false,
  onSubmit,
  styles,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const setupWebNotifications = async () => {
    try {
      if (isSupportedForWeb()) {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          const firebaseToken = await getFirebaseToken();
          if (firebaseToken) {
            await setFcmToken(firebaseToken);
          }
        }
      }
    } catch (error) {
      console.error('Error during web notification setup:', error);
    }
  };

  const handleSubmit = async (values, { setFieldError, setTouched }) => {
    if (!values.mobileNumber) {
      setFieldError('mobileNumber', t('error_mobile_no_blank'));
      setTouched({ mobileNumber: true });
      return false;
    } else if (values.mobileNumber && !values.isPhoneValid) {
      setFieldError(
        'mobileNumber',
        values.phoneError || t('error_mobile_no_not_valid')
      );
      setTouched({ mobileNumber: true });
      return false;
    }

    dispatch(setLoading(true));
    const token = await getToken();
    if (!token) {
      await setupWebNotifications();
    }

    let obj = { mobile_number: values.formattedNumber || '' };
    if (values.name) {
      obj['name'] = values.name;
    }

    onSubmit(obj, queueUserDetail?.id);
  };

  return (
    <View style={styles.sectionContainer}>
      <View style={styles.sectionPartHeadingContainer}>
        <QText style={styles.sectionPartHeadingText}>
          {t('add_phone_no_to_get_notified')}
        </QText>
      </View>
      <Formik
        validationSchema={yup.object().shape({
          countryCode: yup.string().required(t('error_country_required')),
          mobileNumber: yup.string().required(t('error_mobile_no_blank')),
        })}
        initialValues={{
          mobileNumber: '',
          name: queueUserDetail?.name ? queueUserDetail?.name : '',
          countryCode: sessionStorage.getItem('countryCode') || 'IN',
          formattedNumber: '',
          isPhoneValid: false,
        }}
        onSubmit={handleSubmit}
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          values,
          errors,
          touched,
          setFieldValue,
          setFieldError,
          setTouched,
        }) => (
          <>
            <TextInput
              label={t('name')}
              placeholder={t('enter_name')}
              value={values.name}
              onChangeText={handleChange('name')}
              error={!!errors.name && touched.name}
              errorText={errors.name && touched.name ? errors.name : ''}
              maxLength={50}
            />
            <View
              style={[
                styles.internationalPickerContainer,
                { position: 'relative', zIndex: 9999 },
              ]}
            >
              <InternationalPhonePicker
                value={values.mobileNumber}
                countryCode={values.countryCode}
                onChange={(data) => {
                  setFieldValue('mobileNumber', data.phoneNumber);
                  setFieldValue('countryCode', data.countryCode);
                  setFieldValue('formattedNumber', data.formattedNumber);
                  setFieldValue('isPhoneValid', data.isValid);

                  sessionStorage.setItem('countryCode', data.countryCode);

                  if (!data.isValid) {
                    setFieldValue('phoneError', data.validationError);
                  } else {
                    setFieldValue('phoneError', '');
                  }
                }}
                error={
                  (errors.mobileNumber && touched.mobileNumber) ||
                  (!!values.phoneError && touched.mobileNumber)
                }
                errorText={
                  touched.mobileNumber
                    ? values.phoneError ||
                      (errors.mobileNumber ? errors.mobileNumber : '')
                    : ''
                }
                required={true}
                label={t('mobile_number')}
                validateMobileType={true}
              />
            </View>
            <View style={{ marginTop: 5, width: '100%' }}>
              <Button
                mode="contained"
                onPress={handleSubmit}
                disabled={
                  !values.isPhoneValid && values.mobileNumber.length > 0
                }
              >
                {!isDirectAccess ? t('submit') : t('send_otp')}
              </Button>
            </View>
          </>
        )}
      </Formik>
    </View>
  );
};

export default MobileNumberForm;
